import React, { useCallback, useEffect, useRef, useState } from "react";
import { formattextFn } from "../../library/utils";
import { toPlainTemplateText } from "../../helpers/templateHelpers";
import { TEMPLATE_MESSAGE_PREVIEW_LINES } from "../../constant/TemplateConstant";

/**
 * A template's message, clamped to a couple of lines with a See more / See less
 * toggle — the picker rows and the templates table both show it this way.
 *
 * Collapsed it is a plain line-clamped block, so a multi-paragraph template
 * spends both lines on text rather than on the blank one between paragraphs.
 * Expanded it switches to `pre-wrap`, which is where the author's own line
 * breaks matter.
 *
 * "See more" is only offered when the text actually overflows, which cannot be
 * known from the string: the column width varies and the clamp is what decides.
 * So it is measured off the rendered node and re-measured on resize, and a
 * message that fits gets no toggle rather than a dead link.
 */
const TemplateMessagePreview = ({
  message,
  lines = TEMPLATE_MESSAGE_PREVIEW_LINES,
  seeMoreText = "See more",
  seeLessText = "See less",
  // Off by default: the design shows the message as plain text. Turn it on to
  // tint `{mergeField}` tokens the way the composer does.
  highlightMergeFields = false,
  // The picker's rows sit under a 14px title, so their preview is a step down
  // from it; the templates table has no such pairing and reads at the table's
  // own 14px. Passed in rather than decided here — the two surfaces disagree.
  textSizeClassName = "text-[13px]",
  className = "",
}) => {
  const textRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const measure = useCallback(() => {
    const node = textRef.current;
    if (!node) return;
    // Only meaningful while clamped — expanded, scrollHeight always equals
    // clientHeight and the toggle would disappear mid-read.
    if (expanded) return;
    setIsOverflowing(node.scrollHeight > node.clientHeight + 1);
  }, [expanded]);

  useEffect(() => {
    measure();
  }, [measure, message, lines]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return undefined;
    const node = textRef.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [measure]);

  if (!message) return null;

  const style = expanded
    ? { whiteSpace: "pre-wrap" }
    : {
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: lines,
        overflow: "hidden",
      };

  const textClassName = `w-full ${textSizeClassName} leading-5 font-normal text-black/[0.65] break-words`;

  return (
    <div className={`flex flex-col ${className}`}>
      {highlightMergeFields ? (
        <span
          ref={textRef}
          style={style}
          className={textClassName}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: formattextFn(message) }}
        />
      ) : (
        <span ref={textRef} style={style} className={textClassName}>
          {toPlainTemplateText(message)}
        </span>
      )}
      {(isOverflowing || expanded) && (
        <button
          type="button"
          className="w-fit p-0 bg-transparent border-0 cursor-pointer select-none text-[12px] leading-5 text-[#1677ff] hover:text-[#4096ff]"
          onClick={event => {
            // The row itself is clickable in the picker; toggling the preview
            // is not choosing the template.
            event.stopPropagation();
            setExpanded(prev => !prev);
          }}
        >
          {expanded ? seeLessText : seeMoreText}
        </button>
      )}
    </div>
  );
};

export default TemplateMessagePreview;
